// Chosen values as removable chips, with a text box that suggests more as the user types (a
// combobox): pick a suggestion with the mouse or the arrow keys and Enter, or, where free
// values are allowed, type one and press Enter or a comma.
import { X } from "@phosphor-icons/react";
import { type KeyboardEvent, type ReactNode, useId, useState } from "react";

import { cn } from "./cn";

export interface Suggestion {
  value: string;
  /** Shown instead of the value, e.g. a project's name beside its key. */
  label?: ReactNode;
}

export function ChipInput({
  label,
  values,
  onChange,
  suggestions = [],
  onQuery,
  placeholder,
  free = true,
  valid = () => true,
  removeLabel,
  status,
}: {
  label: string;
  values: readonly string[];
  onChange: (next: string[]) => void;
  suggestions?: readonly Suggestion[];
  /** The text typed so far, for the caller to fetch suggestions. */
  onQuery?: (text: string) => void;
  placeholder?: string;
  /** Whether a typed value may be added without a suggestion. */
  free?: boolean;
  valid?: (value: string) => boolean;
  removeLabel: (value: string) => string;
  /** A line under the box: loading, a failure, a hint. */
  status?: ReactNode;
}) {
  const id = useId();
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const shown = suggestions.filter((s) => !values.includes(s.value)).slice(0, 8);
  const type = (next: string) => {
    setText(next);
    setActive(0);
    setOpen(true);
    onQuery?.(next.trim());
  };
  const add = (value: string) => {
    const v = value.trim();
    if (v === "" || values.includes(v) || !valid(v)) return;
    onChange([...values, v]);
    // Closed until the user types again, so the list does not cover the fields below.
    setText("");
    setOpen(false);
    onQuery?.("");
  };
  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      setOpen(true);
      setActive((a) => (shown.length === 0 ? 0 : (a + (e.key === "ArrowDown" ? 1 : shown.length - 1)) % shown.length));
    } else if (e.key === "Enter" || (e.key === "," && free)) {
      e.preventDefault();
      const picked = open ? shown[active] : undefined;
      if (picked) add(picked.value);
      else if (free) add(text);
    } else if (e.key === "Escape") {
      setOpen(false);
    } else if (e.key === "Backspace" && text === "" && values.length > 0) {
      onChange(values.slice(0, -1));
    }
  };
  const invalid = text.trim() !== "" && free && !valid(text.trim());
  return (
    <div className="relative">
      <label htmlFor={id} className="mb-1.5 block text-sm font-medium text-gray-900">
        {label}
      </label>
      <div
        className={cn(
          "flex min-h-10 flex-wrap items-center gap-1.5 rounded-md border border-gray-300 px-2 py-1.5 focus-within:border-gray-1000 focus-within:ring-1 focus-within:ring-gray-1000 dark:bg-gray-50",
          invalid && "border-red focus-within:border-red focus-within:ring-red",
        )}
      >
        {values.map((v) => (
          <span key={v} className="inline-flex max-w-full items-center gap-1 rounded-full bg-primary-lighter py-0.5 pe-1 ps-2.5 text-[13px] font-medium text-primary-dark">
            <span className="truncate">{v}</span>
            <button type="button" onClick={() => onChange(values.filter((x) => x !== v))} aria-label={removeLabel(v)} className="rounded-full p-0.5 hover:bg-primary/15">
              <X weight="bold" className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          id={id}
          role="combobox"
          aria-expanded={open && shown.length > 0}
          aria-controls={`${id}-list`}
          aria-autocomplete="list"
          aria-activedescendant={open && shown[active] ? `${id}-${active}` : undefined}
          aria-invalid={invalid || undefined}
          value={text}
          placeholder={values.length === 0 ? placeholder : undefined}
          onChange={(e) => type(e.target.value)}
          onKeyDown={onKey}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 120)}
          className="h-7 min-w-[8rem] flex-1 border-0 bg-transparent p-0 px-1 text-sm text-gray-900 placeholder:text-gray-500 focus:ring-0"
        />
      </div>
      {open && shown.length > 0 ? (
        <ul id={`${id}-list`} role="listbox" aria-label={label} className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-lg border border-muted bg-gray-0 p-1 shadow-lg dark:bg-gray-100">
          {shown.map((s, i) => (
            <li
              key={s.value}
              id={`${id}-${i}`}
              role="option"
              aria-selected={i === active}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => add(s.value)}
              onMouseEnter={() => setActive(i)}
              className={cn("cursor-pointer rounded-md px-2.5 py-1.5 text-[13px] text-gray-800", i === active && "bg-primary-lighter text-primary-dark")}
            >
              {s.label ?? s.value}
            </li>
          ))}
        </ul>
      ) : null}
      {status ? <div className="mt-1.5 text-[13px] text-gray-500">{status}</div> : null}
    </div>
  );
}
